import { ReactNode } from "react";

import dayjs from "dayjs";
import { Button, Grid, IconButton, Paper, Stack, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { Plus, Trash2 } from "lucide-react";
import { NumberFormatValues, NumericFormat } from "react-number-format";

import { CustomAccordion } from "@components/CustomAccordion";
import { FinancialTable, FinancialTableCell, FinancialTableRow } from "@components/v2/FinancialTable";
import { formatCurrency, formatDateWithWeekday } from "@helpers/formats";

import { PROJECTION_YEARS_COUNT } from "../../types";
import { useParametricosContext } from "../../context/ParametricosContext";
import { FieldRow, ReadonlyValue, TextInput } from "../fields";

const YEAR_COLUMNS = Array.from({ length: PROJECTION_YEARS_COUNT }, (_, i) => i);

const getNoteLabel = (idx: number) => (idx < 26 ? String.fromCharCode(97 + idx) : `${idx + 1}`);

const YearNumberCell = ({ value, onChange, disabled }: { value: number; onChange?: (value: number) => void; disabled?: boolean }) =>
    onChange ? (
        <FinancialTableCell numeric sx={{ minWidth: 110, p: 0.5 }}>
            <NumericFormat
                fullWidth
                size="small"
                thousandSeparator
                displayType="input"
                customInput={TextField}
                autoComplete="off"
                value={value}
                onValueChange={(event: NumberFormatValues) => onChange(event.floatValue ?? 0)}
                disabled={disabled}
                slotProps={{ htmlInput: { style: { textAlign: "right" } } }}
            />
        </FinancialTableCell>
    ) : (
        <FinancialTableCell numeric sx={{ minWidth: 110 }}>
            {formatCurrency(value)}
        </FinancialTableCell>
    );

export default function ProyeccionParametricos() {
    const {
        permissionProjection,
        evaluationDate,
        evaluatorName,
        creditorName,
        sowingTypeName,
        hectares,
        creditQuotaPerHectare,
        dataForm,
        projectionForm,
        projectionTable,
        save,
    } = useParametricosContext();

    const disabled = permissionProjection?.can_edit === false;

    const evaluationYear = dayjs(evaluationDate).year();
    const yearLabels = YEAR_COLUMNS.map((i) => `${evaluationYear + i}`);

    const autoRow = (label: string, values: Array<number | null>, options?: { indent?: boolean; variant?: "section" | "subtotal" }) => (
        <FinancialTableRow variant={options?.variant}>
            <FinancialTableCell indent={options?.indent}>{label}</FinancialTableCell>
            {values.map((value, idx) => (
                <FinancialTableCell key={idx} numeric sx={{ minWidth: 110 }}>
                    {value === null ? "" : formatCurrency(value, "$", 2)}
                </FinancialTableCell>
            ))}
        </FinancialTableRow>
    );

    const manualYearRow = (label: string, fieldName: string, values: Array<number>) => (
        <FinancialTableRow>
            <FinancialTableCell indent>{label}</FinancialTableCell>
            {values.map((value, idx) => (
                <YearNumberCell
                    key={idx}
                    value={value}
                    disabled={disabled}
                    onChange={(newValue) => {
                        const next = [...values];
                        next[idx] = newValue;
                        projectionForm.setFieldValue(fieldName, next);
                    }}
                />
            ))}
        </FinancialTableRow>
    );

    const productionRow = ({ label, children }: { label: string; children: ReactNode }) => (
        <Grid size={3}>
            <Typography variant="body2" sx={{ fontWeight: 700, opacity: 0.55 }}>
                {label}
            </Typography>
            {children}
        </Grid>
    );

    return (
        <Stack sx={{ rowGap: 2, py: 2 }}>
            <Paper variant="section">
                <CustomAccordion
                    title={<Typography sx={{ fontWeight: 700 }}>Proyección de Ingresos y Egresos Para su Capacidad de Pago</Typography>}
                    defaultExpanded
                >
                    <Stack spacing={2}>
                        <FieldRow label="Nombre del Cliente">
                            <ReadonlyValue value={creditorName || "--"} />
                        </FieldRow>

                        <Grid container spacing={2}>
                            {productionRow({ label: "Descripción", children: <ReadonlyValue value={sowingTypeName || "--"} /> })}
                            {productionRow({
                                label: "A.- Superficie que Siembra (ha)",
                                children: <ReadonlyValue value={hectares} />,
                            })}
                            {productionRow({
                                label: "B.- Superficie para Crédito (ha)",
                                children: <ReadonlyValue value={dataForm.values.creditSurfaceHa} />,
                            })}
                            {productionRow({
                                label: "C.- Cuota de Crédito ($/ha)",
                                children: <ReadonlyValue value={formatCurrency(creditQuotaPerHectare)} />,
                            })}
                        </Grid>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section" sx={{ overflowX: "auto" }}>
                <FinancialTable>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700 }}>Concepto</TableCell>
                            {yearLabels.map((label) => (
                                <TableCell key={label} align="right" sx={{ fontWeight: 700, minWidth: 110 }}>
                                    {label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {autoRow("1. Ventas Netas", projectionTable.ventasNetas, { variant: "section" })}
                        {autoRow("1.1 Ingresos ($) Grano", projectionTable.ingresosGrano, { indent: true })}
                        {manualYearRow("1.2 Ingresos por Servicios ($)", "servicesIncome", projectionForm.values.servicesIncome)}
                        {manualYearRow("1.3 Ingresos por Apoyo", "supportIncome", projectionForm.values.supportIncome)}

                        {autoRow("2. Costos Totales", projectionTable.costosTotales, { variant: "section" })}
                        {autoRow("2.1 Costos de Producción ($)", projectionTable.costosProduccion, { indent: true })}
                        {manualYearRow("2.2 Costo de Renta ($)", "rentCost", projectionForm.values.rentCost)}
                        {manualYearRow("2.3 Costos por Servicios ($)", "servicesCost", projectionForm.values.servicesCost)}

                        {autoRow("3. Utilidad Operativa ($) (1-2)", projectionTable.utilidadOperativa, { variant: "subtotal" })}

                        {autoRow("4. Pago Obligaciones Vigentes (a+b+c)", projectionTable.pagoObligaciones, { variant: "section" })}
                        {autoRow("4.1 a).- Capital (Amortización)", projectionTable.capitalAmortizacion, { indent: true })}
                        {autoRow("4.2 b).- Interés de Créd Simple", projectionTable.interesCreditoSimple, { indent: true })}
                        {manualYearRow(
                            "4.3 b).- Interés de Capital de Trabajo CC",
                            "workingCapitalInterest",
                            projectionForm.values.workingCapitalInterest,
                        )}

                        {autoRow("5. Saldo (3 - 4)", projectionTable.saldo1, { variant: "subtotal" })}

                        {autoRow("6. Pago de Crédito Proyectado ($)", projectionTable.pagoCreditoProyectado, {
                            variant: "section",
                        })}
                        {autoRow("6.1 a).- Capital", projectionTable.capitalCredito, { indent: true })}
                        {autoRow("6.2 b).- Interés", projectionTable.interesCredito, { indent: true })}
                        {autoRow("6.3 c).- Intereses de avío/CT", projectionTable.interesAvioCt, { indent: true })}

                        {autoRow("7. Saldo (7 - 8)", projectionTable.saldo2, { variant: "subtotal" })}

                        {manualYearRow("8. Otros Ingresos ($)", "otherIncome", projectionForm.values.otherIncome)}
                        {manualYearRow("9. Otros Gastos ($)", "otherExpenses", projectionForm.values.otherExpenses)}

                        {autoRow("10. Utilidad del EJERCICIO", projectionTable.utilidadEjercicio, { variant: "subtotal" })}

                        <FinancialTableRow variant="subtotal">
                            <FinancialTableCell>11. Capacidad de Pago</FinancialTableCell>
                            {projectionTable.capacidadDePago.map((value, idx) => (
                                <FinancialTableCell key={idx} numeric sx={{ minWidth: 110 }}>
                                    {value === null ? "" : value.toFixed(2)}
                                </FinancialTableCell>
                            ))}
                        </FinancialTableRow>
                    </TableBody>
                </FinancialTable>
            </Paper>

            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>Notas</Typography>}>
                    <Stack spacing={2}>
                        {projectionForm.values.notes.length === 0 ? (
                            <Typography variant="body2">Sin notas capturadas</Typography>
                        ) : (
                            projectionForm.values.notes.map((note, idx) => (
                                <Grid container spacing={2} key={idx} sx={{ alignItems: "center" }}>
                                    <Grid size={1}>
                                        <Typography variant="body1" sx={{ fontWeight: 700, opacity: 0.55 }}>
                                            {`${getNoteLabel(idx)}).-`}
                                        </Typography>
                                    </Grid>
                                    <Grid size={10}>
                                        <TextInput
                                            value={note}
                                            onChange={(value) => {
                                                const next = [...projectionForm.values.notes];
                                                next[idx] = value;
                                                projectionForm.setFieldValue("notes", next);
                                            }}
                                            disabled={disabled}
                                            multiline
                                        />
                                    </Grid>
                                    <Grid size={1}>
                                        <IconButton
                                            onClick={() =>
                                                projectionForm.setFieldValue(
                                                    "notes",
                                                    projectionForm.values.notes.filter((_, i) => i !== idx),
                                                )
                                            }
                                            disabled={disabled}
                                        >
                                            <Trash2 size={18} />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            ))
                        )}
                        <Button
                            variant="outlined"
                            startIcon={<Plus size={16} />}
                            sx={{ alignSelf: "flex-start" }}
                            disabled={disabled}
                            onClick={() => projectionForm.setFieldValue("notes", [...projectionForm.values.notes, ""])}
                        >
                            Agregar Nota
                        </Button>
                    </Stack>
                </CustomAccordion>
            </Paper>

            <Paper variant="section">
                <CustomAccordion title={<Typography sx={{ fontWeight: 700 }}>Justificación del Proyecto</Typography>} defaultExpanded>
                    <Typography variant="body1">
                        {`SE SOLICITA FINANCIAMIENTO PARA: ${dataForm.values.investmentConceptCt}`}
                    </Typography>
                </CustomAccordion>
            </Paper>

            <Paper variant="section" sx={{ p: 2 }}>
                <Typography sx={{ fontWeight: 700 }}>EVALUO:</Typography>
                <Typography variant="body1">{evaluatorName}</Typography>
                <Typography variant="body2" color="text.secondary">
                    {`Fecha de Evaluación: ${formatDateWithWeekday(evaluationDate)}`}
                </Typography>
            </Paper>

            <Button
                variant="contained"
                sx={{ alignSelf: "flex-end" }}
                disabled={disabled || !projectionForm.dirty}
                onClick={() => save()}
            >
                Guardar
            </Button>
        </Stack>
    );
}
